import {
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link } from "react-router-dom";

import {
  AlertCircle,
  Check,
  ChevronDown,
  Eye,
  Loader2,
  LockKeyhole,
  RefreshCw,
  Search,
  Settings2,
  ShieldCheck,
  Sparkles,
  Users,
  X,
} from "lucide-react";

import platformFeaturesApi from "../../api/platformFeaturesApi";
import { getApiErrorMessage } from "../../api/axiosClient";
import { useLanguage } from "../../contexts/LanguageContext";

import "../../styles/platform-features/platform-features.css";

const MODE_OPTIONS = [
  "disabled",
  "all",
  "selected",
];

const USER_STATES = [
  "inherit",
  "enabled",
  "disabled",
];

const translateFallback = (
  t,
  key,
  fallback,
) => {
  const value = t(key);

  return value === key
    ? fallback
    : value;
};

const getInitial = (name) => {
  return String(name || "?")
    .trim()
    .slice(0, 1)
    .toUpperCase();
};

function PlatformFeatureSelect({
  value,
  options,
  onChange,
  ariaLabel,
}) {
  const selectRef = useRef(null);

  const [open, setOpen] =
    useState(false);

  const selectedOption =
    options.find(
      (option) =>
        option.value === value,
    ) || options[0];

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleOutsideClick = (
      event,
    ) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(
          event.target,
        )
      ) {
        setOpen(false);
      }
    };

    const handleEscape = (
      event,
    ) => {
      if (
        event.key === "Escape"
      ) {
        setOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleOutsideClick,
    );

    document.addEventListener(
      "keydown",
      handleEscape,
    );

    return () => {
      document.removeEventListener(
        "mousedown",
        handleOutsideClick,
      );

      document.removeEventListener(
        "keydown",
        handleEscape,
      );
    };
  }, [open]);

  return (
    <div
      ref={selectRef}
      className={`platform-feature-custom-select ${
        open
          ? "platform-feature-custom-select--open"
          : ""
      }`}
    >
      <button
        type="button"
        className="platform-feature-custom-select__trigger"
        onClick={() =>
          setOpen(
            (state) => !state,
          )
        }
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span>
          {selectedOption?.label ||
            value}
        </span>

        <ChevronDown
          size={15}
        />
      </button>

      {open && (
        <div
          className="platform-feature-custom-select__menu"
          role="listbox"
        >
          {options.map(
            (option) => {
              const selected =
                option.value ===
                value;

              return (
                <button
                  key={
                    option.value
                  }
                  type="button"
                  className={`platform-feature-custom-select__option ${
                    selected
                      ? "platform-feature-custom-select__option--active"
                      : ""
                  }`}
                  onClick={() => {
                    setOpen(
                      false,
                    );

                    onChange(
                      option.value,
                    );
                  }}
                  role="option"
                  aria-selected={
                    selected
                  }
                >
                  <span>
                    {
                      option.label
                    }
                  </span>

                  {selected && (
                    <Check
                      size={14}
                    />
                  )}
                </button>
              );
            },
          )}
        </div>
      )}
    </div>
  );
}

export default function PlatformFeatures() {
  const { t } =
    useLanguage();

  const [
    direction,
    setDirection,
  ] = useState(
    document.documentElement.dir ||
      "ltr",
  );

  const isRtl =
    direction === "rtl";

  const [
    features,
    setFeatures,
  ] = useState([]);

  const [
    selectedId,
    setSelectedId,
  ] = useState(null);

  const [
    users,
    setUsers,
  ] = useState([]);

  const [
    search,
    setSearch,
  ] = useState("");

  const [
    loading,
    setLoading,
  ] = useState(true);

  const [
    usersLoading,
    setUsersLoading,
  ] = useState(false);

  const [
    error,
    setError,
  ] = useState("");

  // Keep this page synchronized with the global application direction.
  useEffect(() => {
    const updateDirection =
      () => {
        setDirection(
          document.documentElement
            .dir || "ltr",
        );
      };

    updateDirection();

    const observer =
      new MutationObserver(
        updateDirection,
      );

    observer.observe(
      document.documentElement,
      {
        attributes: true,
        attributeFilter: [
          "dir",
          "lang",
        ],
      },
    );

    return () => {
      observer.disconnect();
    };
  }, []);

  const selectedFeature =
    useMemo(
      () =>
        features.find(
          (feature) =>
            feature.id ===
            selectedId,
        ) ||
        features[0] ||
        null,
      [
        features,
        selectedId,
      ],
    );

  const modeOptions =
    useMemo(
      () =>
        MODE_OPTIONS.map(
          (mode) => ({
            value: mode,

            label:
              translateFallback(
                t,
                `platformFeatures.modes.${mode}`,
                mode,
              ),

            help:
              translateFallback(
                t,
                `platformFeatures.modeHelp.${mode}`,
                mode,
              ),
          }),
        ),
      [t],
    );

  const userStateOptions =
    useMemo(
      () =>
        USER_STATES.map(
          (state) => ({
            value: state,

            label:
              translateFallback(
                t,
                `platformFeatures.userStates.${state}`,
                state,
              ),
          }),
        ),
      [t],
    );

  const summary =
    useMemo(() => {
      return features.reduce(
        (
          totals,
          feature,
        ) => {
          const mode =
            feature.mode ||
            "disabled";

          return {
            total:
              totals.total + 1,

            all:
              totals.all +
              (mode === "all"
                ? 1
                : 0),

            selected:
              totals.selected +
              (mode ===
              "selected"
                ? 1
                : 0),

            disabled:
              totals.disabled +
              (mode ===
              "disabled"
                ? 1
                : 0),
          };
        },
        {
          total: 0,
          all: 0,
          selected: 0,
          disabled: 0,
        },
      );
    }, [features]);

  const loadFeatures =
    async () => {
      try {
        setLoading(true);
        setError("");

        const response =
          await platformFeaturesApi.list();

        const data =
          response.data.data ||
          [];

        setFeatures(data);

        if (
          !selectedId &&
          data[0]
        ) {
          setSelectedId(
            data[0].id,
          );
        }
      } catch (
        requestError
      ) {
        setError(
          getApiErrorMessage(
            requestError,
          ),
        );
      } finally {
        setLoading(false);
      }
    };

  const loadUsers =
    async (
      featureId =
        selectedFeature?.id,
    ) => {
      if (!featureId) {
        return;
      }

      try {
        setUsersLoading(true);
        setError("");

        const response =
          await platformFeaturesApi.users(
            featureId,
            {
              search:
                search.trim() ||
                undefined,

              per_page: 100,
            },
          );

        setUsers(
          response.data.data ||
            [],
        );
      } catch (
        requestError
      ) {
        setError(
          getApiErrorMessage(
            requestError,
          ),
        );
      } finally {
        setUsersLoading(false);
      }
    };

  useEffect(() => {
    loadFeatures();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      selectedFeature?.id
    ) {
      loadUsers(
        selectedFeature.id,
      );
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedFeature?.id,
  ]);

  const updateMode =
    async (mode) => {
      if (
        !selectedFeature
      ) {
        return;
      }

      try {
        setError("");

        const response =
          await platformFeaturesApi.update(
            selectedFeature.id,
            mode,
          );

        const updated =
          response.data.data;

        setFeatures(
          (current) =>
            current.map(
              (feature) =>
                feature.id ===
                updated.id
                  ? updated
                  : feature,
            ),
        );

        await loadUsers(
          selectedFeature.id,
        );
      } catch (
        requestError
      ) {
        setError(
          getApiErrorMessage(
            requestError,
          ),
        );
      }
    };

  const updateUserState =
    async (
      userId,
      state,
    ) => {
      if (
        !selectedFeature
      ) {
        return;
      }

      try {
        setError("");

        const response =
          await platformFeaturesApi.updateUser(
            selectedFeature.id,
            userId,
            state,
          );

        setUsers(
          (current) =>
            current.map(
              (user) =>
                user.id === userId
                  ? {
                      ...user,

                      override:
                        response.data
                          .data
                          .override,

                      effective:
                        response.data
                          .data
                          .effective,
                    }
                  : user,
            ),
        );
      } catch (
        requestError
      ) {
        setError(
          getApiErrorMessage(
            requestError,
          ),
        );
      }
    };

  return (
    <main
      className={`platform-features-page ${
        isRtl
          ? "platform-features-page--rtl"
          : "platform-features-page--ltr"
      }`}
      dir={direction}
    >
      <header className="platform-features-header">
        <div className="platform-features-header__copy">
          <span className="platform-features-header__eyebrow">
            <Settings2
              size={17}
            />

            {translateFallback(
              t,
              "platformFeatures.eyebrow",
              "Platform controls",
            )}
          </span>

          <h1>
            {translateFallback(
              t,
              "platformFeatures.title",
              "Platform features",
            )}
          </h1>

          <p>
            {translateFallback(
              t,
              "platformFeatures.description",
              "Control which SaaS features are available for all managers or only selected accounts.",
            )}
          </p>
        </div>

        <button
          type="button"
          className="platform-features-refresh-button"
          onClick={
            loadFeatures
          }
          disabled={
            loading
          }
        >
          {loading ? (
            <Loader2
              size={17}
              className="platform-features-spin"
            />
          ) : (
            <RefreshCw
              size={17}
            />
          )}

          <span>
            {translateFallback(
              t,
              "common.refresh",
              "Refresh",
            )}
          </span>
        </button>
      </header>

      {error && (
        <div
          className="platform-features-alert"
          role="alert"
        >
          <AlertCircle
            size={18}
          />

          <span>
            {error}
          </span>
        </div>
      )}

      <section className="platform-features-summary-grid">
        <article>
          <span className="platform-features-summary-grid__icon">
            <Sparkles
              size={18}
            />
          </span>

          <div>
            <strong>
              {summary.total}
            </strong>

            <p>
              {translateFallback(
                t,
                "platformFeatures.summary.total",
                "Total features",
              )}
            </p>
          </div>
        </article>

        <article>
          <span className="platform-features-summary-grid__icon">
            <Check
              size={18}
            />
          </span>

          <div>
            <strong>
              {summary.all}
            </strong>

            <p>
              {translateFallback(
                t,
                "platformFeatures.summary.all",
                "Enabled for all",
              )}
            </p>
          </div>
        </article>

        <article>
          <span className="platform-features-summary-grid__icon">
            <Users
              size={18}
            />
          </span>

          <div>
            <strong>
              {
                summary.selected
              }
            </strong>

            <p>
              {translateFallback(
                t,
                "platformFeatures.summary.selected",
                "Selected users",
              )}
            </p>
          </div>
        </article>

        <article>
          <span className="platform-features-summary-grid__icon">
            <LockKeyhole
              size={18}
            />
          </span>

          <div>
            <strong>
              {
                summary.disabled
              }
            </strong>

            <p>
              {translateFallback(
                t,
                "platformFeatures.summary.disabled",
                "Disabled",
              )}
            </p>
          </div>
        </article>
      </section>

      {loading ? (
        <section className="platform-features-loading">
          <Loader2
            size={32}
            className="platform-features-spin"
          />

          <strong>
            {translateFallback(
              t,
              "common.loading",
              "Loading...",
            )}
          </strong>
        </section>
      ) : (
        <div className="platform-features-layout">
          <aside className="platform-features-list">
            <div className="platform-features-list__head">
              <strong>
                {translateFallback(
                  t,
                  "platformFeatures.featureList",
                  "Feature list",
                )}
              </strong>

              <small>
                {
                  features.length
                }
              </small>
            </div>

            <div className="platform-features-list__items">
              {features.map(
                (feature) => (
                  <button
                    type="button"
                    key={
                      feature.id
                    }
                    className={
                      selectedFeature?.id ===
                      feature.id
                        ? "is-active"
                        : ""
                    }
                    onClick={() =>
                      setSelectedId(
                        feature.id,
                      )
                    }
                  >
                    <span className="platform-feature-icon">
                      <ShieldCheck
                        size={19}
                      />
                    </span>

                    <span className="platform-feature-list__copy">
                      <strong>
                        {
                          feature.name
                        }
                      </strong>

                      <small dir="ltr">
                        {
                          feature.key
                        }
                      </small>
                    </span>

                    <em
                      className={`is-${feature.mode}`}
                    >
                      {translateFallback(
                        t,
                        `platformFeatures.modes.${feature.mode}`,
                        feature.mode,
                      )}
                    </em>
                  </button>
                ),
              )}
            </div>
          </aside>

          {selectedFeature && (
            <section className="platform-feature-panel">
              <div className="platform-feature-panel__header">
                <div className="platform-feature-panel__copy">
                  <span>
                    <ShieldCheck
                      size={17}
                    />

                    <bdi dir="ltr">
                      {
                        selectedFeature.key
                      }
                    </bdi>
                  </span>

                  <h2>
                    {
                      selectedFeature.name
                    }
                  </h2>

                  <p>
                    {translateFallback(
                      t,
                      "platformFeatures.modeDescription",
                      "Choose how this feature should be exposed across your manager accounts.",
                    )}
                  </p>
                </div>

                <span
                  className={`platform-feature-status is-${selectedFeature.mode}`}
                >
                  {translateFallback(
                    t,
                    `platformFeatures.modes.${selectedFeature.mode}`,
                    selectedFeature.mode,
                  )}
                </span>
              </div>

              <div className="platform-feature-mode-grid">
                {modeOptions.map(
                  (option) => (
                    <button
                      type="button"
                      key={
                        option.value
                      }
                      className={
                        selectedFeature.mode ===
                        option.value
                          ? "is-selected"
                          : ""
                      }
                      onClick={() =>
                        updateMode(
                          option.value,
                        )
                      }
                    >
                      <span className="platform-feature-radio">
                        {selectedFeature.mode ===
                        option.value ? (
                          <Check
                            size={14}
                          />
                        ) : null}
                      </span>

                      <span className="platform-feature-mode-grid__copy">
                        <strong>
                          {
                            option.label
                          }
                        </strong>

                        <small>
                          {
                            option.help
                          }
                        </small>
                      </span>
                    </button>
                  ),
                )}
              </div>

              <div className="platform-feature-users-header">
                <div className="platform-feature-users-header__copy">
                  <span className="platform-feature-users-header__icon">
                    <Users
                      size={20}
                    />
                  </span>

                  <div>
                    <strong>
                      {translateFallback(
                        t,
                        "platformFeatures.users.title",
                        "User overrides",
                      )}
                    </strong>

                    <small>
                      {translateFallback(
                        t,
                        "platformFeatures.users.description",
                        "Override feature access for specific manager accounts.",
                      )}
                    </small>
                  </div>
                </div>

                <form
                  onSubmit={(
                    event,
                  ) => {
                    event.preventDefault();
                    loadUsers();
                  }}
                >
                  <Search
                    size={17}
                  />

                  <input
                    value={
                      search
                    }
                    onChange={(
                      event,
                    ) =>
                      setSearch(
                        event.target
                          .value,
                      )
                    }
                    placeholder={translateFallback(
                      t,
                      "platformFeatures.users.search",
                      "Search users...",
                    )}
                  />

                  <button type="submit">
                    {translateFallback(
                      t,
                      "common.search",
                      "Search",
                    )}
                  </button>
                </form>
              </div>

              <div className="platform-feature-users-table-wrap">
                <table className="platform-feature-users-table">
                  <thead>
                    <tr>
                      <th>
                        {translateFallback(
                          t,
                          "platformFeatures.users.user",
                          "User",
                        )}
                      </th>

                      <th>
                        {translateFallback(
                          t,
                          "platformFeatures.users.status",
                          "Status",
                        )}
                      </th>

                      <th>
                        {translateFallback(
                          t,
                          "platformFeatures.users.override",
                          "Override",
                        )}
                      </th>

                      <th>
                        {translateFallback(
                          t,
                          "platformFeatures.users.actions",
                          "Actions",
                        )}
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {usersLoading ? (
                      <tr>
                        <td colSpan="4">
                          <div className="platform-features-table-state">
                            <Loader2
                              size={20}
                              className="platform-features-spin"
                            />

                            <span>
                              {translateFallback(
                                t,
                                "common.loading",
                                "Loading...",
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : users.length ===
                      0 ? (
                      <tr>
                        <td colSpan="4">
                          <div className="platform-features-table-state">
                            <Users
                              size={22}
                            />

                            <span>
                              {translateFallback(
                                t,
                                "platformFeatures.users.empty",
                                "No users found.",
                              )}
                            </span>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      users.map(
                        (user) => (
                          <tr
                            key={
                              user.id
                            }
                          >
                            <td>
                              <div className="platform-feature-user">
                                <span>
                                  {getInitial(
                                    user.name,
                                  )}
                                </span>

                                <div>
                                  <strong>
                                    {
                                      user.name
                                    }
                                  </strong>

                                  <small dir="ltr">
                                    {
                                      user.email
                                    }
                                  </small>
                                </div>
                              </div>
                            </td>

                            <td>
                              <span
                                className={`platform-feature-effective ${
                                  user
                                    .effective
                                    ?.available
                                    ? "is-enabled"
                                    : user
                                          .effective
                                          ?.locked
                                      ? "is-locked"
                                      : "is-hidden"
                                }`}
                              >
                                {user
                                  .effective
                                  ?.available ? (
                                  <Check
                                    size={14}
                                  />
                                ) : user
                                    .effective
                                    ?.locked ? (
                                  <LockKeyhole
                                    size={14}
                                  />
                                ) : (
                                  <X
                                    size={14}
                                  />
                                )}

                                {user
                                  .effective
                                  ?.available
                                  ? translateFallback(
                                      t,
                                      "platformFeatures.users.enabled",
                                      "Enabled",
                                    )
                                  : user
                                      .effective
                                      ?.locked
                                    ? translateFallback(
                                        t,
                                        "platformFeatures.users.locked",
                                        "Locked",
                                      )
                                    : translateFallback(
                                        t,
                                        "platformFeatures.users.hidden",
                                        "Hidden",
                                      )}
                              </span>
                            </td>

                            <td>
                              <PlatformFeatureSelect
                                value={
                                  user.override ||
                                  "inherit"
                                }
                                options={
                                  userStateOptions
                                }
                                onChange={(
                                  state,
                                ) =>
                                  updateUserState(
                                    user.id,
                                    state,
                                  )
                                }
                                ariaLabel={translateFallback(
                                  t,
                                  "platformFeatures.users.override",
                                  "Override",
                                )}
                              />
                            </td>

                            <td>
                              <div className="platform-feature-user-actions">
                                <Link
                                  to={`/users/${user.id}`}
                                  title={translateFallback(
                                    t,
                                    "platformFeatures.users.viewDetails",
                                    "View user details",
                                  )}
                                >
                                  <Eye
                                    size={16}
                                  />

                                  <span>
                                    {translateFallback(
                                      t,
                                      "platformFeatures.users.details",
                                      "Details",
                                    )}
                                  </span>
                                </Link>
                              </div>
                            </td>
                          </tr>
                        ),
                      )
                    )}
                  </tbody>
                </table>
              </div>
            </section>
          )}
        </div>
      )}
    </main>
  );
}